import { useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { StatusMessagePopover } from '@/components/shared/StatusMessagePopover';
import { PublicationErrorDialog } from '@/dialogs/publication-error/PublicationErrorDialog';
import { useTranslation } from '@/hooks/useTranslation';

const Status: Record<string, 'info' | 'success' | 'error' | 'warning' | 'neutral'> = {
    ABORTED: 'error',
    ACCEPTED: 'success',
    ACTIVE: 'success',
    BLOCKED: 'info',
    BUSY: 'warning',
    CHANGED: 'neutral',
    DEFERRED: 'neutral',
    DELETING: 'warning',
    DELIVERED: 'info',
    ENDED: 'success',
    ERROR: 'error',
    ESTABLISHED: 'success',
    EVALUATING: 'warning',
    QUEUED: 'warning',
    EXCEPTION: 'warning',
    EXPORTING: 'info',
    FAILED: 'error',
    FATAL: 'error',
    FINISHED: 'success',
    IDLE: 'neutral',
    IGNORED: 'neutral',
    IMPORTED: 'success',
    IMPORTING: 'info',
    INACTIVE: 'neutral',
    INITIALIZING: 'warning',
    INVITED: 'info',
    NEW: 'neutral',
    OPEN: 'warning',
    PENDING: 'info',
    PENDING_PROCESSING: 'neutral',
    PROCESSED: 'success',
    PROCESSING: 'info',
    READY: 'success',
    RECEIVED: 'info',
    REJECTED: 'warning',
    REVIEWED: 'error',
    RUNNING: 'info',
    SENDING: 'info',
    STARTED: 'info',
    SUCCESS: 'success',
    TIMED_OUT: 'error',
    UNEXPECTED_DELAY: 'warning',
    UPDATING: 'info',
    WARNING: 'warning'
};

export const StatusColumn = ({
    status,
    finalStatus,
    message,
    destination = '',
    date = '',
}: {
    status: string;
    finalStatus?: string;
    message?: string;
    destination?: string;
    date?: string;
}) => {
    const { t } = useTranslation();
    const [reportOpen, setReportOpen] = useState(false);
    const labelStatus = finalStatus ?? status;
    const displayStatus = finalStatus ? `${t(status)} \u2192 ${t(finalStatus)}` : t(status);
    const badge = (
        <Badge variant={Status[labelStatus]} className="tw-shrink-0 tw-whitespace-nowrap">
            {displayStatus}
        </Badge>
    );
    const details = message?.trim() ?? '';

    if (!details) {
        return badge;
    }

    return (
        <>
            <StatusMessagePopover
                badge={badge}
                header={<Badge variant={Status[labelStatus]}>{displayStatus}</Badge>}
                message={details}
                ariaLabel={t('PUBLICATION_ERROR.DETAILS')}
                onShowReport={() => setReportOpen(true)}
            />
            {reportOpen && (
                <PublicationErrorDialog
                    message={details}
                    destination={destination}
                    date={date}
                    onClose={() => setReportOpen(false)}
                />
            )}
        </>
    );
};
